import { Link } from "wouter";
import { ArrowLeft, Calendar, User, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { Spinner } from "@/components/ui/spinner";

export default function ArticleDetail() {
  const articleId = (() => {
    const match = window.location.pathname.match(/^\/articles\/(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  })();

  const { data: article, isLoading, error } = trpc.articles.getById.useQuery(
    { id: articleId! },
    { enabled: !!articleId }
  );

  if (!articleId) {
    return (
      <div className="min-h-screen bg-white px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-600 text-lg">Article not found</p>
          <Link href="/">
            <Button className="mt-4">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white px-4 py-16 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-white px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-600 text-lg">Failed to load article</p>
          <Link href="/">
            <Button className="mt-4">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <Link href="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
            
            {/* Language Display - English Only */}
            <div className="flex gap-2">
              <Badge className="bg-blue-600 text-white">English</Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 py-12">
        {/* Featured Image */}
        {article.featuredImage && (
          <div className="mb-8 rounded-lg overflow-hidden bg-gray-100 h-96">
            <img
              src={article.featuredImage}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Title */}
        <div>
          <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {article.englishTitle || article.title}
          </h1>
          {!article.englishTitle && (
            <Badge className="mb-4 bg-yellow-50 text-yellow-700 hover:bg-yellow-100">
              Original language shown (English version unavailable)
            </Badge>
          )}
        </div>

        {/* Metadata */}
        <div className="flex flex-wrap gap-6 mb-8 pb-8 border-b">
          {article.category && (
            <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-100">
              {article.category}
            </Badge>
          )}
          
          <div className="flex items-center text-gray-600">
            <User className="w-4 h-4 mr-2" />
            <span>{article.author}</span>
          </div>

          <div className="flex items-center text-gray-600">
            <Calendar className="w-4 h-4 mr-2" />
            <span>{article.publishedAt ? new Date(article.publishedAt).toLocaleDateString() : 'Not published'}</span>
          </div>

          {article.tags && (
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-gray-600" />
              <div className="flex gap-2">
                {(typeof article.tags === 'string' ? JSON.parse(article.tags) : article.tags).map((tag: string) => (
                  <Badge key={tag} variant="outline" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Summary */}
        {(article.englishSummary || article.summary) && (
          <div className="mb-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-lg text-gray-700 italic">
              {article.englishSummary || article.summary}
            </p>
          </div>
        )}

        {/* Content */}
        <div className="prose prose-lg max-w-none mb-12 text-gray-700 leading-relaxed">
          {(article.englishContent || article.content)
            .split(/\r?\n/)
            .map((line: string, index: number) => {
              const trimmed = line.trim();
              if (!trimmed) return null;
              if (trimmed.startsWith('### ')) {
                return <h3 key={index} className="text-2xl font-bold mt-6 mb-3">{trimmed.slice(4)}</h3>;
              }
              if (trimmed.startsWith('## ')) {
                return <h2 key={index} className="text-3xl font-bold mt-8 mb-4">{trimmed.slice(3)}</h2>;
              }
              if (trimmed.startsWith('# ')) {
                return <h1 key={index} className="text-4xl font-bold mt-10 mb-5">{trimmed.slice(2)}</h1>;
              }
              return <p key={index}>{trimmed}</p>;
            })}
        </div>

        {/* Related Articles CTA */}
        <div className="text-center py-12 border-t">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Interested in more articles?
          </h3>
          <Link href="/">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
              Back to Home
            </Button>
          </Link>
        </div>
      </article>
    </div>
  );
}
